import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus, AlertCircle, RefreshCw, Filter } from 'lucide-react';
import { BusinessSettingsLayout } from '@/components/business-settings/BusinessSettingsLayout';
import { PolicyListCard } from '@/components/leave-policies/PolicyListCard';
import { CreatePolicyDialog } from '@/components/leave-policies/CreatePolicyDialog';
import { EditPolicyDialog } from '@/components/leave-policies/EditPolicyDialog';
import { EmployeeAssignmentDialog } from '@/components/leave-policies/EmployeeAssignmentDialog';
import { BalanceViewDialog } from '@/components/leave-policies/BalanceViewDialog';

import { RolloverManagement } from '@/components/leave/RolloverManagement';
import { PolicyDeactivationDialog } from '@/components/leave-policies/PolicyDeactivationDialog';
import { PolicyDeletionDialog } from '@/components/leave-policies/PolicyDeletionDialog';
import { useLeavePolicies, useLeavePolicyMutations } from '@/hooks/leave';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { LeavePolicyWithRules } from '@/services/leave/leavePolicyTypes';

export default function LeavePoliciesSettings() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<LeavePolicyWithRules | null>(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [selectedPolicyForAssignment, setSelectedPolicyForAssignment] = useState<{ id: string; name: string } | null>(null);
  const [selectedPolicyName, setSelectedPolicyName] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('policies');
  const [showInactivePolicies, setShowInactivePolicies] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [policyToDeactivate, setPolicyToDeactivate] = useState<{ id: string; name: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<{ id: string; name: string } | null>(null);
  
  const { data: policies, isLoading, error, refetch } = useLeavePolicies();
  const { 
    deleteLeavePolicy, 
    reactivateLeavePolicy, 
    permanentlyDeleteLeavePolicy 
  } = useLeavePolicyMutations();

  const handleEditPolicy = (policy: LeavePolicyWithRules) => {
    setEditingPolicy(policy);
  };

  const handleDeactivatePolicy = async (policyId: string) => {
    const policy = policies?.find(p => p.id === policyId);
    if (policy) {
      setPolicyToDeactivate({ id: policyId, name: policy.name });
      setDeactivateDialogOpen(true);
    }
  };

  const handleConfirmDeactivation = async () => {
    if (policyToDeactivate) {
      await deleteLeavePolicy.mutateAsync(policyToDeactivate.id);
      setPolicyToDeactivate(null);
    }
  };

  const handleReactivatePolicy = async (policyId: string) => {
    try {
      await reactivateLeavePolicy.mutateAsync(policyId);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleDeletePolicy = (policyId: string) => {
    const policy = policies?.find(p => p.id === policyId);
    if (policy) {
      setPolicyToDelete({ id: policyId, name: policy.name });
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDeletion = () => {
    if (policyToDelete) {
      permanentlyDeleteLeavePolicy.mutate(policyToDelete.id);
      setPolicyToDelete(null);
    }
  };

  const handleAssignEmployees = (policyId: string) => {
    const policy = policies?.find(p => p.id === policyId);
    if (policy) {
      setSelectedPolicyForAssignment({ id: policyId, name: policy.name });
      setAssignmentDialogOpen(true);
    }
  };

  const handleViewBalances = (policyId: string) => {
    setSelectedPolicyForAssignment({ id: policyId, name: policies?.find(p => p.id === policyId)?.name || '' });
    setBalanceDialogOpen(true);
  };

  // Filter policies based on show inactive toggle
  const filteredPolicies = policies?.filter(policy => 
    showInactivePolicies ? true : policy.is_active
  );

  if (error) {
    return (
      <BusinessSettingsLayout
        title="Leave Policies"
        description="Configure leave types, accrual rules, and approval workflows"
      >
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load leave policies: {error.message}
          </AlertDescription>
        </Alert>
      </BusinessSettingsLayout>
    );
  }

  return (
    <BusinessSettingsLayout
      title="Leave Policies"
      description="Configure leave types, accrual rules, and approval workflows"
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="policies">Leave Policies</TabsTrigger>
          <TabsTrigger value="rollover">Rollover Management</TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="space-y-6">
          {/* Header Actions */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold">Leave Policies</h2>
              <p className="text-muted-foreground">
                Manage your organization's leave policies and settings
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Filter and Create Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Switch
                id="show-inactive"
                checked={showInactivePolicies}
                onCheckedChange={setShowInactivePolicies}
              />
              <Label htmlFor="show-inactive" className="text-sm">
                <Filter className="h-4 w-4 inline mr-1" />
                Show inactive policies
              </Label>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Policy
            </Button>
          </div>

          {/* Policy List */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Loading skeleton */}
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-6 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-full" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="h-4 bg-muted rounded w-1/2" />
                      <div className="h-4 bg-muted rounded w-2/3" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredPolicies && filteredPolicies.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPolicies.map((policy) => (
                <PolicyListCard
                  key={policy.id}
                  policy={policy}
                    onEdit={handleEditPolicy}
                    onDeactivate={handleDeactivatePolicy}
                    onReactivate={handleReactivatePolicy}
                    onDelete={handleDeletePolicy}
                    onAssignEmployees={handleAssignEmployees}
                    onViewBalances={handleViewBalances}
                />
              ))}
            </div>
          ) : showInactivePolicies && policies && policies.length > 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                    <Filter className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium">No Inactive Policies</h3>
                    <p className="text-muted-foreground">
                      All policies are currently active. Inactive policies will appear here when deactivated.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                    <Plus className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium">No Leave Policies Yet</h3>
                    <p className="text-muted-foreground">
                      Get started by creating your first leave policy. You can configure accrual rules, 
                      balance limits, and approval workflows.
                    </p>
                  </div>
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Policy
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Active Policies</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {policies?.filter(p => p.is_active).length || 0}
                </div>
                <p className="text-sm text-muted-foreground">
                  Active policies available for assignments
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Leave Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {new Set(policies?.map(p => p.leave_type)).size || 0}
                </div>
                <p className="text-sm text-muted-foreground">
                  Different types of leave configured
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Total Policies</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {policies?.length || 0}
                </div>
                <p className="text-sm text-muted-foreground">
                  All policies (active and inactive)
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>


        <TabsContent value="rollover" className="space-y-6">
          <RolloverManagement />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {/* Create Policy Dialog */}
      <CreatePolicyDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* Edit Policy Dialog */}
      <EditPolicyDialog
        policy={editingPolicy}
        open={!!editingPolicy}
        onOpenChange={(open) => !open && setEditingPolicy(null)}
      />

      {/* Employee Assignment Dialog */}
      <EmployeeAssignmentDialog
        policyId={selectedPolicyForAssignment?.id || null}
        policyName={selectedPolicyForAssignment?.name || ''}
        open={assignmentDialogOpen}
        onOpenChange={(open) => {
          setAssignmentDialogOpen(open);
          if (!open) setSelectedPolicyForAssignment(null);
        }}
        mode="manage"
      />

      {/* Balance View Dialog */}
      <BalanceViewDialog
        policyId={selectedPolicyForAssignment?.id || null}
        open={balanceDialogOpen}
        onOpenChange={(open) => {
          setBalanceDialogOpen(open);
          if (!open) setSelectedPolicyForAssignment(null);
        }}
      />

      {/* Policy Deactivation Dialog */}
      <PolicyDeactivationDialog
        policyId={policyToDeactivate?.id || null}
        policyName={policyToDeactivate?.name || ''}
        open={deactivateDialogOpen}
        onOpenChange={(open) => {
          setDeactivateDialogOpen(open);
          if (!open) setPolicyToDeactivate(null);
        }}
        onConfirm={handleConfirmDeactivation}
        onAssignEmployees={handleAssignEmployees}
        onViewBalances={handleViewBalances}
      />

      {/* Policy Deletion Dialog */}
      <PolicyDeletionDialog
        policyId={policyToDelete?.id || null}
        policyName={policyToDelete?.name || ''}
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setPolicyToDelete(null);
        }}
        onConfirm={handleConfirmDeletion}
      />
    </BusinessSettingsLayout>
  );
}
